// Shared types for the Meeting Intelligence (Read.ai pipeline) feature module.
// These mirror the API record shapes returned by the read-ai services.

export type DistributionStatus =
  | "pending"
  | "assigned"
  | "needs_review"
  | "escalated"
  | "rejected"
  | string;

/**
 * The assignment lifecycle, as it is since approval was removed on 2026-09-18
 * (plan/2026-09-18_suggestions-and-ado-as-a-ticket-database.md, ruling R2 —
 * mirrored from the API's `read-ai-assignments/assignment-status.ts`):
 *
 *   available   distribution resolved an assignee; the item is in that
 *               developer's Gantry queue. NOTHING external has happened yet.
 *   accepted    a developer took it in Gantry. THAT is when HQ files the Azure
 *               DevOps work item — nothing HQ's UI does creates one.
 *   completed   the PR is merged; HQ closed the work item with the PR on it.
 *   rejected    declined. Terminal.
 *
 * The last three are the approval era's and still appear in a migrated
 * database: `assigned` is `accepted` under the old name (it has a work item and
 * a developer may still be on it), `approving` is a crashed approval nothing
 * writes any more, and `pending_review` should not survive the 20260918000001
 * migration but is tolerated so a restored backup is never unreadable.
 */
export type AssignmentStatus =
  | "available"
  | "accepted"
  | "completed"
  | "rejected"
  | "assigned"
  | "approving"
  | "pending_review"
  | string;

/**
 * Where an action item came from.
 *  - `read_ai`  — LEGACY. Read.ai's own candidate items, attached at ingest
 *                 before 2026-09-18. The vendor's list is now stored as
 *                 SUGGESTIONS ({@link MeetingSuggestion}) and nothing writes
 *                 this source any more; the rows that carry it are being closed
 *                 as duplicates.
 *  - `read_ai_promoted` — a suggestion a named human decided IS work. The text
 *                 is the vendor's; the decision is a person's, which is exactly
 *                 why it is not spelled `read_ai`.
 *  - `llm`      — HQ's in-line extraction (gated by `readAi.localActionItems`)
 *  - `gantry`   — zach-gantry's deep analysis; the only source that fills the
 *                 repo/package/scope/timestamp fields below
 */
export type ActionItemSource =
  | "read_ai"
  | "read_ai_promoted"
  | "llm"
  | "gantry"
  | string;

export interface ReadAiParticipant {
  id: number;
  meetingId: number;
  name?: string | null;
  email?: string | null;
}

export interface ReadAiActionItem {
  id: number;
  meetingId: number;
  meetingTitle?: string | null;
  source: ActionItemSource;
  text?: string | null;
  context?: string | null;
  priority?: string | null;
  assigneeName?: string | null;
  assigneeEmail?: string | null;
  dueDate?: string | null;
  confidence?: number | null;
  /**
   * LIFECYCLE status — `'open'` | `'closed'`. A different column from
   * {@link distributionStatus}, which records how far distribution got with the
   * item; an item can be `escalated` with no assignment and still be closed by
   * a human. Absent on an API that predates the close path, and absence reads
   * as OPEN (see `isClosedActionItem` in ./actionItemClose).
   */
  status?: string | null;
  /**
   * Why it was closed — `done` | `duplicate` | `superseded` | `wont_do`, plus
   * who/when/what evidence. EVERY close field is optional: an older API omits
   * them all, and an open item has none of them. See ./actionItemClose for the
   * vocabulary, and note that closing NEVER creates an Azure DevOps work item —
   * that is what makes it the right verb for work somebody already did.
   */
  closedReason?: string | null;
  closedBy?: number | null;
  closedAt?: string | null;
  closedEvidence?: string | null;
  duplicateOfItemId?: number | null;
  distributionStatus?: DistributionStatus;
  /**
   * Operator ruling A (2026-11-02): the OPEN client questions filed against this
   * item's meeting — a flag, never a hold. Served on the meeting's list (find)
   * only, narrowed to what this person may see; empty on a closed item.
   */
  openQuestionIds?: number[];
  resolvedUserId?: number | null;
  resolvedUserName?: string | null;
  /**
   * Client module (`client_modules.id`) this item concerns, resolved at
   * extraction. RESTRICTED to the owning client's own modules — `validateModule`
   * refuses another client's module.
   */
  moduleId?: string | null;
  moduleName?: string | null;
  /**
   * GOVERNED attribution (Coordinator catalog) stamped at extraction —
   * deliberately NOT restricted to the client's own modules, so an item may name
   * any assignable governed module in the estate.
   *
   * `governedSlug` set with `moduleId` null is therefore not a defect: it is the
   * "this client is asking for a module they do not run yet" signal. Returned by
   * `read-ai-action-items`, and present on the raw rows embedded in
   * `read-ai-meetings.get(id)`.
   */
  governedSlug?: string | null;
  governedRepo?: string | null;
  assignmentId?: number | null;
  /**
   * Deep-analysis detail, present only on `source: "gantry"` items — HQ's own
   * in-line extraction cannot produce these. Treat every one as optional: an
   * item from any other source has them all null, and that is normal, not a
   * gap to flag in the UI.
   */
  /** Where in the meeting the item was raised, e.g. "00:14:32" (NVARCHAR(50)). */
  transcriptTimestamp?: string | null;
  /** Where in the recording, when one was analysed. */
  videoTimestamp?: string | null;
  /** Repository path the work touches. */
  repoPath?: string | null;
  /** Package/workspace the work belongs to. */
  packageName?: string | null;
  /** `universal` vs `client-specific` — whether the work belongs upstream in a
   *  module or in one client's deployment. */
  scope?: string | null;
  /** The analysis run that produced the item, for tracing back to its report. */
  analysisRunId?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * One meeting as the ANALYSIS QUEUE offers it to a producer (Gantry).
 *
 * This is not `ReadAiMeeting`. It is the far narrower projection
 * `meeting-analysis-queue` serves — deliberately curated, because the queue
 * hands rows to a third party. Keep it a separate type: widening it by
 * accident is how HQ's internal bookkeeping ends up on somebody else's wire.
 */
export interface AnalysisQueueMeeting {
  id: number;
  readAiSessionId: string;
  title: string;
  startTime?: string | null;
  endTime?: string | null;
  clientId?: string | null;
  ownerName?: string | null;
  ownerEmail?: string | null;
  /**
   * Where the meeting came from. **NULL MEANS THE READ.AI WEBHOOK** — the
   * ordinary real meeting — not "unknown". `manual` is a human-uploaded
   * transcript (also real); `email` is an uploaded thread and is NOT a meeting.
   */
  sourceType?: string | null;
  /** HQ's stored transcript, flattened. Present on every offered row. */
  transcriptText?: string | null;
  /** The raw Read.ai body, when HQ kept one. Null = no per-turn timings. */
  transcriptSourceDocumentId?: string | null;
  analysisState: string;
}

/** What the queue page carries alongside the meetings. */
export interface AnalysisQueuePage {
  meetings: AnalysisQueueMeeting[];
  total: number;
  /**
   * Which Read.ai sources HQ is configured to fetch on a producer's behalf.
   * CONFIG-level, not per meeting — both keys are always present. This is the
   * screen's answer to "can we pass video to Gantry at all yet?".
   */
  readAiSources: { transcript: boolean; recording: boolean };
}

/**
 * What THIS caller may do about a meeting's privacy (#4028) — `read-ai-meetings`
 * `get` only. UI gating: the API re-checks both on every change.
 */
export interface MeetingPrivacyRights {
  /** The meeting's HQ owner or an administrator. */
  mayChange: boolean;
  /** Who last changed it, when known. */
  changedByName: string | null;
}

export interface ReadAiMeeting {
  /**
   * PRIVATE MEETINGS (#4028). A private meeting is served only to the people who
   * were in it and to administrators; to anyone else the API answers as though
   * it did not exist, so a page that has one in hand belongs to someone allowed
   * to see it. Absent on an API that predates the feature: read as public.
   */
  isPrivate?: boolean;
  privacyChangedBy?: number | null;
  privacyChangedAt?: string | null;
  privacy?: MeetingPrivacyRights;
  /**
   * Deep-analysis lifecycle: pending | claimed | analyzed | failed | skipped.
   * `failed` is the one worth showing — nothing retries it automatically.
   */
  analysisState?: string | null;
  /** Why the last analysis attempt failed. */
  analysisError?: string | null;
  /**
   * A recording an operator attached because Read.ai published none (or the
   * wrong one). The producer is served THIS in preference to Read.ai's.
   *
   * `null` and `undefined` mean different things and the UI keeps them apart:
   * null is "no recording attached", undefined is an API that predates the
   * column — the same distinction the analysis `sources` field turns on.
   */
  manualRecordingUrl?: string | null;
  manualRecordingNote?: string | null;
  manualRecordingAddedAt?: string | null;
  /**
   * The copy archived into HQ's own blob storage (uploaded by an operator, or
   * swept in from Read.ai / the attached URL). Same null/undefined distinction
   * as above. While unarchived, `recordingArchiveError` carries the last
   * archive failure, if any — a diagnostic, not a UI error state.
   */
  recordingFileId?: string | null;
  recordingArchivedAt?: string | null;
  recordingArchiveError?: string | null;
  id: number;
  readAiSessionId: string;
  /**
   * Where the meeting came from. **NULL MEANS THE READ.AI WEBHOOK** — the
   * ordinary real meeting — NOT "unknown": the Read.ai ingest path never writes
   * this column, so the genuine case is the one that leaves it empty. `manual`
   * is a human-uploaded transcript (also real); `email` is an uploaded thread
   * and is NOT a meeting; anything else is admin free text from a connector and
   * is not a closed set.
   *
   * A consumer filtering on this must DENY-LIST. An allow-list of "known good"
   * values refuses every genuine Read.ai meeting.
   */
  sourceType?: string | null;
  title: string;
  startTime?: string | null;
  endTime?: string | null;
  ownerName?: string | null;
  ownerEmail?: string | null;
  summary?: string | null;
  reportUrl?: string | null;
  transcriptText?: string | null;
  /** The CLIENT this meeting is held with (mapping target — projects are gone). */
  clientId?: string | null;
  clientName?: string | null;
  /** How the client mapping was reached. 'internal' means a human marked the
   *  meeting IntraSync-internal, so it currently has no client. It is NOT
   *  "no client, and never will": assigning a client to an internal meeting is
   *  a supported path — `meeting-mappings` `action:'assign'` sets `clientId`
   *  and rewrites `mappingStatus` to `'manual'` in one update, and the meeting
   *  then processes in full through the normal pipeline (pinned end to end by
   *  `packages/api/test/e2e/read-ai-pipeline.e2e.test.ts`). What 'internal' IS
   *  terminal for is HQ's own client-keyed extraction WHILE it holds: no action
   *  items, no client memory, no distribution, no ADO work item from that path.
   *  Items the DEEP analysis returns for an internal meeting are internal work
   *  with no client, and can still be approved. */
  mappingStatus?: "auto" | "needs_review" | "manual" | "internal" | null;
  /** Why this meeting is still in the mapping queue (unmapped meetings only). */
  mappingReason?: MappingReason | null;
  processed?: boolean;
  processingError?: string | null;
  /** Operational state added to the API projection on 2026-09-04 for the
   *  Pipeline tracker. `processingAttempts === 0` beside a `processingError`
   *  is what distinguishes a HOLD (attempt refunded) from a FAILURE; without
   *  it the two are indistinguishable. See the API's ReadAiMeetingRecord. */
  awaitingTranscript?: boolean;
  processingAttempts?: number | null;
  lastAttemptAt?: string | null;
  processingStatus?: string | null;
  recordingArchiveAttempts?: number | null;
  analysisClaimedAt?: string | null;
  analysisClaimedBy?: string | null;
  sourceConnectionId?: string | null;
  externalId?: string | null;
  processedAt?: string | null;
  /** Every action item this meeting produced, closed ones INCLUDED. */
  actionItemCount?: number;
  /** How many of those are still open — i.e. still work. Absent on an API that
   *  predates the close path; fall back to actionItemCount, never to 0. */
  openActionItemCount?: number;
  createdAt?: string;
  updatedAt?: string;
  // Embedded on get(id):
  participants?: ReadAiParticipant[];
  actionItems?: ReadAiActionItem[];
}

/**
 * One row of the `meeting-analysis` read service — a single LLM task's output
 * for a meeting (summary / happiness-score / topics / risks / …). `outputJson`
 * is the raw JSON string returned by the routed provider; `score` is populated
 * for scoring tasks (e.g. client happiness, 0–100). `status` is "ok" | "error".
 */
export interface MeetingAnalysisRow {
  id: number;
  meetingId: number;
  taskKey: string;
  outputJson?: string | null;
  score?: number | null;
  providerId?: string | null;
  model?: string | null;
  status: string;
  errorMessage?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

/** A corporate email domain mapped to a partner (`partner-email-domains`). */
export interface PartnerEmailDomain {
  id: number;
  partnerId: string;
  domain: string;
  verified: boolean;
}

/**
 * One relationship-ledger row (`relationship-items`) — an expectation / need /
 * commitment. On Partner 360 we query `subjectType='partner'`: what the partner
 * expects of us, needs from us, or has committed to (the "harmony" tracking).
 */
export interface RelationshipItem {
  id: number;
  kind: string;
  subjectType: string;
  subjectId?: string | null;
  counterpartyType?: string | null;
  counterpartyId?: string | null;
  text: string;
  status: string;
  priority?: string | null;
  sourceMeetingId?: number | null;
  dueDate?: string | null;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
}

/**
 * Why a meeting could not be auto-mapped to a client — the `mappingReason` the
 * `meeting-mappings` find returns for each queued row. Drives the "why is this
 * here" explanation in the Mapping Queue.
 */
export type MappingReason =
  | "no_known_domain"
  | "multiple_clients"
  | "partner_only"
  | "internal_only"
  | "domain_conflict"
  /** Not the auto-mapper's: an operator sent an internal meeting back. */
  | "unmarked_internal";

/** One row of the `meeting-mappings` find (an unmapped meeting awaiting triage). */
export interface MeetingMappingRow {
  meetingId: number;
  title: string | null;
  ownerName: string | null;
  ownerEmail: string | null;
  startTime: string | null;
  createdAt: string | null;
  /** WHY the meeting is unmapped — explained to the operator in the queue. */
  mappingReason?: MappingReason | null;
  /** Attendee domains seen on the meeting (context for the operator). */
  domains?: string[] | null;
  /** Candidate clients when `mappingReason` is `multiple_clients`. */
  candidateClientIds?: string[] | null;
}

/** Patch body for `meeting-mappings` — assign a meeting to a CLIENT. */
export interface MeetingMappingAssignPayload {
  action: "assign";
  clientId: string;
  /** Optional external parties also present in the meeting. */
  partnerIds?: string[];
}

/** Patch body for `meeting-mappings` — resolve a queued meeting as an
 *  IntraSync-internal meeting (terminal; no client, leaves the queue). */
export interface MeetingMappingInternalPayload {
  action: "mark-internal";
}

/** Patch body for `meeting-mappings` — send an internal meeting back to the
 *  queue. Only an internal meeting with no client; the API answers 409
 *  otherwise. */
export interface MeetingMappingUnmarkPayload {
  action: "unmark-internal";
}

/**
 * Something a meeting SETTLED — an architecture choice, a rejected option, a
 * confirmed convention. Recorded separately from action items on purpose: work
 * that nobody can ever close sits in the review queue forever and teaches
 * reviewers to dismiss it.
 *
 * Only the Gantry deep analysis writes these, so a meeting HQ processed itself
 * has none. Empty is normal, not a gap.
 */
export interface MeetingDecision {
  id: number;
  meetingId: number;
  text: string;
  /** Why it was decided — the reasoning that would otherwise be relitigated. */
  context?: string | null;
  /** Where in the meeting it was settled. A STRING: a producer may know
   *  "around 14:05" without a precise offset. */
  transcriptTimestamp?: string | null;
  analysisRunId?: string | null;
  createdAt?: string | null;
}

/**
 * What an analysis run stated it READ. The positive counterpart of a gap.
 *
 * `used` is the answer to "was the video in this analysis?" for `kind:
 * 'recording'`. `origin` separates the same artefact from different places —
 * Read.ai's transcript and HQ's stored copy are not interchangeable.
 */
export interface MeetingAnalysisSource {
  kind: string;
  used: boolean;
  origin?: string | null;
  detail?: string | null;
}

/** What an analysis run stated it did NOT do. Always present: the API refuses a
 *  result payload with no `gaps` field at all. */
export interface MeetingAnalysisGap {
  code: string;
  detail: string;
}

/**
 * One accepted deep-analysis run — the provenance behind a summary card.
 *
 * The reason this type exists at all: two pipelines write summaries for the same
 * meeting (HQ's in-line LLM pass and a producer's deep pass), and until this was
 * exposed the only way to tell them apart in the product was a caption.
 */
export interface MeetingAnalysisRun {
  id: number;
  meetingId: number;
  analysisRunId: string;
  /** WHICH pipeline produced it — e.g. `zach-gantry`. */
  producer: string;
  producedAt?: string | null;
  gaps: MeetingAnalysisGap[];
  /**
   * `null` means NOT STATED — the producer did not send the field. It is NOT
   * the same as `[]` (a producer saying it read nothing), and the UI must never
   * render null as "no video": that would answer on behalf of a producer that
   * never spoke.
   */
  sources: MeetingAnalysisSource[] | null;
  itemCount: number;
  decisionCount: number;
  reportDocumentId?: string | null;
  createdAt?: string | null;
}

export interface ReadAiAssignment {
  id: number;
  status: AssignmentStatus;
  title: string;
  /**
   * [hub] review round 1: the OPEN client inquiries holding this item. An
   * assignment stays `available` while a question holds its item, so this is
   * what says "not yet" — Accept is refused (409 held-by-inquiry) until it is
   * empty.
   */
  heldByInquiryIds?: number[];
  /**
   * The source item's own `distributionStatus`. `pending` on an `available`
   * assignment means a question holds the item — even when that question is
   * one this person may not see (operator ruling B, 2026-11-02), so is not
   * named in `heldByInquiryIds`.
   */
  itemDistributionStatus?: string | null;
  /**
   * Operator ruling A (2026-11-02): the OPEN client questions filed against this
   * work's meeting — a flag ("open client question"), never a hold: a question
   * holds only the items it names (`heldByInquiryIds`). Only the questions this
   * person may see; empty on finished work.
   */
  openQuestionIds?: number[];
  context?: string | null;
  priority?: string | null;
  dueDate?: string | null;
  userId?: number | null;
  assigneeName?: string | null;
  assigneeEmail?: string | null;
  meetingId?: number | null;
  meetingTitle?: string | null;
  /**
   * Extraction confidence of the source action item, as a 0–100 integer (the
   * same `read_ai_action_items.confidence` the model produced and that the
   * auto-approve threshold is compared against). Assignments have no confidence
   * of their own — it rides along from `read_ai_assignments.actionItemId`, so it
   * is absent whenever the API has not joined the source item.
   */
  confidence?: number | null;
  /**
   * Deep-analysis detail, riding along from the source action item on the same
   * join as `confidence`. Present only when zach-gantry produced the item
   * (`itemSource === "gantry"`); null for HQ's own extraction and for any
   * assignment with no source item.
   *
   * ABSENCE IS NORMAL. Render these when they exist and say nothing when they
   * do not — a "missing" badge on every pre-Gantry item would be noise on the
   * overwhelming majority of the queue.
   */
  repoPath?: string | null;
  packageName?: string | null;
  /** `universal` (belongs upstream in a module) vs `client-specific`. */
  scope?: string | null;
  /** Where in the meeting the item was raised, e.g. "00:14:32". */
  transcriptTimestamp?: string | null;
  /** Which producer wrote the source item — `llm` | `gantry` | `read_ai`. */
  itemSource?: ActionItemSource | null;
  /** Client module / tool / integration this item is tracked against — the ONLY
   *  work attribution left now that projects are gone. */
  moduleId?: string | null;
  moduleName?: string | null;
  /**
   * GOVERNED attribution (Coordinator catalog) stamped at extraction — carried
   * even when `moduleId` stayed null (ambiguity, or the client doesn't have the
   * module yet). Feeds the tracked-module suggestion in the Review Queue.
   */
  governedSlug?: string | null;
  governedRepo?: string | null;
  /** Client the item resolves to — scopes the module picker. */
  clientId?: string | null;
  clientName?: string | null;
  /** External party (partner) the item resolves to, when applicable. */
  partnerId?: string | null;
  partnerName?: string | null;
  externalWorkItemId?: string | null;
  externalWorkItemUrl?: string | null;
  reviewedBy?: number | null;
  reviewedAt?: string | null;
  /**
   * LEGACY, and it no longer describes anything that can happen: the confidence
   * gate it recorded was removed with approval on 2026-09-18. True on rows
   * approved before that date, false on everything since.
   */
  autoApproved?: boolean;
  /** The developer who ACCEPTED the work in Gantry — a user id, not a name. */
  acceptedBy?: number | null;
  acceptedAt?: string | null;
  /**
   * What HQ managed to tell Azure DevOps: `created` | `deferred` | `closed` |
   * `close-refused` | `creating`. NULL on an `available` row is the CORRECT and
   * expected state — ADO hears about work when a developer accepts it, never
   * before — so a null here must not be rendered as a problem.
   */
  adoState?: string | null;
  /** Why the last ADO write did not land. Reads beside a `deferred` state. */
  adoError?: string | null;
  /** When the work shipped. Null until completed. */
  completedAt?: string | null;
  /**
   * The PR the completion recorded — written by whoever completed it (Gantry,
   * an operator), so render it only through `httpUrlOrNull`. A completion may
   * carry no PR at all.
   */
  completedPrUrl?: string | null;
  /**
   * [pr-feedback] The PR this item is being built in, as its Gantry reported it
   * while it was OPEN, with the last merge status and PR build HQ heard from
   * Azure DevOps. Absent on an HQ API older than this; null = no PR reported.
   */
  openPr?: ReadAiOpenPr | null;
  createdAt?: string;
  /**
   * [retarget] Served by `get` only (the Action Item page): the source action
   * item's id, which `developer-action-items` is keyed by, and that item's
   * NEWEST retarget. Absent on an HQ API older than this; null = never moved.
   */
  actionItemId?: number | null;
  retarget?: ReadAiRetarget | null;
}

/** [retarget] One side of a retarget (API `analysis-handoff/action-item-retarget.ts` `ServedTarget`). */
export interface ReadAiRetargetTarget {
  governedSlug: string | null;
  governedRepo: string | null;
  clientCode: string | null;
  scope: string | null;
  packageName: string | null;
}

/** [retarget] The newest retarget of an item (API `RetargetRecord`). */
export interface ReadAiRetarget {
  id: number;
  at: string;
  /** Who acted, as recorded ("chaz via erick@…"). The authority was the item's owner. */
  by: string;
  target: "module" | "client" | null;
  work: "config" | "data" | "bespoke" | null;
  reason: string | null;
  previous: ReadAiRetargetTarget;
  current: ReadAiRetargetTarget;
  undoUntil: string | null;
  /** When it was undone, or null. */
  undone: string | null;
}

/** [pr-feedback] The open PR on an assignment (API `pr-feedback/open-pr.ts` `OpenPr`). */
export interface ReadAiOpenPr {
  id: number;
  /** Written by a developer's Gantry — render only through `httpUrlOrNull`. */
  url: string | null;
  repository: string | null;
  project: string | null;
  sourceBranch: string | null;
  at: string | null;
  mergeStatus: string | null;
  mergeStatusAt: string | null;
  /** Null until a build event for this PR reached HQ — NOT "no build ran". */
  build: null | {
    id: number;
    result: string | null;
    at: string | null;
    url: string | null;
    /** Why it failed, read from the build's timeline; or why it could not be read. */
    failure: string | null;
    failureState: "pending" | "read" | "unreadable" | null;
  };
}

/** Edits an operator may apply in the same call as an accept. */
export interface AssignmentApproveEdits {
  title?: string;
  priority?: string;
  dueDate?: string | null;
  userId?: number;
  /** Client module to track the item against; null clears it. */
  moduleId?: string | null;
}

/**
 * Statuses an OPERATOR may still act on from HQ — decline, reassign, or set the
 * tracked module. Mirrors the API's `INERT_ASSIGNMENT_STATUSES`: nobody has
 * accepted the work and nothing exists for it in Azure DevOps, so changing it
 * leaves no trace outside HQ. An `accepted` row is deliberately excluded — a
 * developer has taken it and a work item may exist.
 */
export const INERT_ASSIGNMENT_STATUSES: readonly AssignmentStatus[] = [
  "available",
  "pending_review",
];

/**
 * True when an operator's decline / reassign / set-module controls should
 * render. This replaced `isAssignmentPending`, which asked whether an item was
 * waiting for an approval that no longer exists.
 */
export const isAssignmentInert = (status?: AssignmentStatus): boolean =>
  status != null && INERT_ASSIGNMENT_STATUSES.includes(status);

/** Statuses that are no longer work. Mirrors the API's terminal set. */
export const isAssignmentTerminal = (status?: AssignmentStatus): boolean =>
  status === "completed" || status === "rejected";

/** True for work a developer has taken — `assigned` is the legacy spelling. */
export const isAssignmentAccepted = (status?: AssignmentStatus): boolean =>
  status === "accepted" || status === "assigned";

/** How a status reads to a human, legacy spellings folded into what they mean. */
export const assignmentStatusLabel = (status?: AssignmentStatus): string => {
  switch (status) {
    // `pending_review` falls through: a row the 20260918000001 migration has
    // not reached yet IS available work.
    case "available":
    case "pending_review":
      return "Available";
    case "accepted":
    case "assigned":
      return "Accepted";
    case "completed":
      return "Completed";
    case "rejected":
      return "Declined";
    case "approving":
      // A crashed approval from before 2026-09-18. Naming it is the point: it
      // is the one state that needs a human and can no longer be reached.
      return "Stuck (pre-2026-09-18 approval)";
    default:
      return status ? status.replace(/_/g, " ") : "Unknown";
  }
};

/**
 * ONE OF READ.AI'S OWN SUGGESTIONS — what the transcription vendor thought it
 * heard somebody agree to do (`meeting-suggestions`).
 *
 * IT IS NOT WORK. It is stored as evidence of what the vendor heard, it is
 * excluded from every work count, from distribution and from the developer
 * feed, and the ONLY way it becomes an action item is a human promoting it.
 * See plan/2026-09-18_suggestions-and-ado-as-a-ticket-database.md.
 */
export interface MeetingSuggestion {
  id: number;
  meetingId: number;
  meetingTitle?: string | null;
  /** Which ingest path carried it: `read_ai`, a connection's key, or `upload`. */
  source: string;
  text: string;
  /**
   * The vendor's own item object, verbatim, as a raw JSON string — NULL means
   * the path had nothing but a string, not that the vendor sent `{}`.
   */
  sourcePayloadJson?: string | null;
  promoted: boolean;
  /** The action item it became. A BREADCRUMB: a re-analysis can delete it. */
  promotedItemId?: number | null;
  promotedBy?: number | null;
  promotedByFirstName?: string | null;
  promotedByLastName?: string | null;
  promotedAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

/**
 * What `PATCH /meeting-suggestions/:id { action: 'promote' }` answers with.
 *
 * `alreadyPromoted: true` IS A SUCCESS — it means somebody already promoted
 * this one and the call created nothing. Treating it as an error would tell an
 * operator their click failed when the item they wanted already exists.
 */
export interface SuggestionPromoteOutcome {
  suggestion: MeetingSuggestion;
  actionItemId: number | null;
  alreadyPromoted: boolean;
}

/** Full name of whoever promoted a suggestion, or null when nobody has. */
export const suggestionPromoterName = (
  suggestion: MeetingSuggestion
): string | null => {
  const name = [suggestion.promotedByFirstName, suggestion.promotedByLastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  if (name) return name;
  return suggestion.promotedBy != null ? `User #${suggestion.promotedBy}` : null;
};

/**
 * What is wrong with a distributed item's ROUTING — the list that replaced the
 * approval queue (`action-routing-issues`). Mirrors the API's
 * `read-ai/action-routing/routing-issues.rules.ts`.
 *
 *   unassigned          nobody matched the assignee the analysis named.
 *   unroutable          the assignee is fine; the module→ADO-project mapping
 *                       is missing, and it only bites once somebody accepts.
 *   work-item-deferred  everything routes; ADO itself refused or was down. The
 *                       accept STANDS and HQ is retrying.
 *   module-not-declared the item is on a module its client does not run, so it
 *                       went to that module's owner: the wrong module, or one
 *                       the client wants and has not declared (2026-11-01).
 */
export type RoutingIssueKind =
  | "unassigned"
  | "unroutable"
  | "work-item-deferred"
  | "module-not-declared";

export interface RoutingIssue {
  kind: RoutingIssueKind;
  /** 1 = nothing is moving; 3 = a gap that bites when somebody accepts. */
  severity: number;
  reason: string;
  /** What to do about it. Always present — the API guarantees it. */
  fix: string;
  actionItemId: number | null;
  assignmentId: number | null;
  meetingId: number | null;
  meetingTitle: string | null;
  clientId: string | null;
  clientName: string | null;
  title: string | null;
  /** Null on an `unassigned` row — that IS the problem. */
  assigneeUserId: number | null;
  assigneeName: string | null;
  governedSlug: string | null;
  moduleId: string | null;
  assignmentStatus: string | null;
  since: string | null;
}

/** The `action-routing-issues` find envelope — an object, not an array. */
export interface RoutingIssuesResponse {
  issues: RoutingIssue[];
  counts: Record<RoutingIssueKind, number>;
  total: number;
  /** What an EMPTY list means, in the API's own words. Rendered verbatim. */
  note: string;
  /** True when either scan hit the cap, so `total` is a floor, not a count. */
  truncated: boolean;
  /** Declared-module gaps on active clients; absent from an older API. */
  moduleProfiles?: ClientModuleProfiles;
}

/**
 * Active clients HQ cannot route by (Implementation Check-in 2026-09-09,
 * meeting 141 item 6). Mirrors `ClientModuleProfiles` in the API's
 * read-ai/action-routing/action-routing-issues.class.ts.
 */
export interface ClientModuleProfiles {
  clientsWithoutModules: Array<{
    clientId: string;
    clientName: string | null;
    clientCode: string | null;
  }>;
  unresolvedModules: Array<{
    clientId: string;
    clientName: string | null;
    clientCode: string | null;
    moduleId: string;
    moduleKey: string;
    displayName: string | null;
  }>;
  /** False: no governed catalog is synced, so declared rows were not judged. */
  catalogSynced: boolean;
  truncated: boolean;
  /** False: the profiles could not be read, and both lists mean nothing. */
  available: boolean;
}

/** How a routing kind reads in a chip. */
export const ROUTING_ISSUE_KIND_LABELS: Record<RoutingIssueKind, string> = {
  unassigned: "Nobody owns it",
  unroutable: "No Azure DevOps project",
  "work-item-deferred": "Work item deferred",
  "module-not-declared": "Not one of the client's modules",
};

/**
 * Format an extraction confidence for display. The API stores confidence as a
 * 0–100 INTEGER (`read_ai_action_items.confidence`, the same scale as the
 * auto-approve threshold), so it is rendered as-is — never rescaled. Returns
 * null when there is no confidence to show.
 */
export const formatConfidence = (value?: number | null): string | null =>
  value == null || Number.isNaN(value)
    ? null
    : `${Math.round(Math.min(100, Math.max(0, value)))}%`;

/** Chip/text color band for a 0–100 confidence, matching the pipeline's tiers. */
export const confidenceColor = (value: number): string =>
  value >= 80 ? "#2e7d32" : value >= 50 ? "#f57c00" : "#c62828";
